import { ArrowRight, Clock3, Gift, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getItemWithExpiration } from '../../lib/utils';
import {
    useGiftCandidatesQuery,
    useGiftHistoryQuery,
    useGiftPointAccountQuery,
    useGiftSummaryQuery,
} from '../../services/useGifticonQueryService';

const SEGMENT_BADGES: Record<string, string> = {
    birthday: '생일',
    rereg: '재등록',
    absent: '이탈 위험',
    milestone: '마일스톤',
    newMember: '신규',
    attendance: '출석 우수',
    anniversary: '기념일',
};

const DashboardGifticonSummary = () => {
    const navigate = useNavigate();
    const accessToken: string = getItemWithExpiration('authorization') ?? '';
    const pointAccount = useGiftPointAccountQuery({ accessToken });
    const summary = useGiftSummaryQuery({ accessToken });
    const monthHistory = useGiftHistoryQuery({ accessToken, query: { period: 'MONTH', page: 1, size: 1 } });
    const candidates = useGiftCandidatesQuery({
        accessToken,
        segments: 'birthday,rereg,absent,milestone,newMember,attendance,anniversary',
    });

    const balance = pointAccount.data?.balance ?? 0;
    const unconfirmedAmount = pointAccount.data?.unconfirmedAmount ?? 0;
    const monthCount = monthHistory.data?.summary.count ?? summary.data?.monthSentCount ?? 0;
    const monthAmount = monthHistory.data?.summary.totalAmount ?? 0;
    const scheduledCount = summary.data?.autoScheduled.count ?? 0;
    const scheduledAmount = summary.data?.autoScheduled.amount ?? 0;
    const recommendations = candidates.data?.slice(0, 3).map((candidate) => ({
        id: candidate.memberId,
        name: candidate.name,
        reason: candidate.reason,
        category: SEGMENT_BADGES[candidate.segmentKey] ?? '추천',
    })) ?? [];
    const recommendCount = candidates.data?.length ?? 0;

    // 로딩 중엔 '-', 에러 시 안내 문구, 그 외에는 실제 값을 보여준다 — 가짜 목업 폴백은 쓰지 않는다
    const pointValueText = pointAccount.isError ? '불러오지 못했어요' : pointAccount.isLoading ? '-' : `${balance.toLocaleString()}P`;
    const pointDescText = pointAccount.isError || pointAccount.isLoading ? '' : `입금 확인 중 ${unconfirmedAmount.toLocaleString()}원`;
    const monthLoading = monthHistory.isLoading || summary.isLoading;
    const monthError = monthHistory.isError && summary.isError;
    const monthValueText = monthError ? '불러오지 못했어요' : monthLoading ? '-' : `${monthCount}건`;
    const monthDescText = monthError || monthLoading ? '' : `총 ${monthAmount.toLocaleString()}P 사용`;
    const scheduledValueText = summary.isError ? '불러오지 못했어요' : summary.isLoading ? '-' : `${scheduledCount}건`;
    const scheduledDescText = summary.isError || summary.isLoading ? '' : `예정 금액 ${scheduledAmount.toLocaleString()}P`;
    const recommendCountText = candidates.isError ? '-' : candidates.isLoading ? '-' : `${recommendCount}명`;

    const goToGifticon = (tab: 'home' | 'send' | 'history') => {
        navigate(`/gifticon?tab=${tab}`);
    };

    const openRecommendedSendModal = () => {
        navigate('/gifticon?tab=home&action=send-timing-all');
    };

    return (
        <Card>
            <Header>
                <TitleArea>
                    <Title>기프티콘 선물</Title>
                    <NewBadge>NEW</NewBadge>
                </TitleArea>
                <HistoryButton type="button" onClick={() => goToGifticon('history')}>
                    전체 발송 내역
                    <ArrowRight size={14} />
                </HistoryButton>
            </Header>

            <SummaryGrid>
                <SummaryItem $tone="blue">
                    <SummaryLabel>사용 가능 포인트</SummaryLabel>
                    <SummaryValue>{pointValueText}</SummaryValue>
                    {pointDescText && <SummaryDescription>{pointDescText}</SummaryDescription>}
                </SummaryItem>
                <SummaryItem $tone="default">
                    <SummaryLabel>이번 달 발송</SummaryLabel>
                    <SummaryValue>{monthValueText}</SummaryValue>
                    {monthDescText && <SummaryDescription>{monthDescText}</SummaryDescription>}
                </SummaryItem>
                <SummaryItem $tone="orange">
                    <SummaryLabel>예약 발송</SummaryLabel>
                    <SummaryValue>{scheduledValueText}</SummaryValue>
                    {scheduledDescText && (
                        <SummaryDescription>
                            <Clock3 size={12} />
                            {scheduledDescText}
                        </SummaryDescription>
                    )}
                </SummaryItem>
            </SummaryGrid>

            <RecommendationPanel>
                <RecommendationHeader>
                    <RecommendationTitle>
                        <Sparkles size={15} />
                        오늘 선물 추천 {recommendCountText}
                    </RecommendationTitle>
                    <RecommendationDescription>발송 전 대상과 상품을 확인할 수 있어요.</RecommendationDescription>
                </RecommendationHeader>

                <RecommendationGrid>
                    {recommendations.length > 0 ? recommendations.map((recommendation) => (
                        <RecommendationItem key={recommendation.id}>
                            <Avatar>{recommendation.name.slice(0, 1)}</Avatar>
                            <RecommendationText>
                                <b>{recommendation.name}</b>
                                <span>{recommendation.reason}</span>
                            </RecommendationText>
                            <CategoryBadge>{recommendation.category}</CategoryBadge>
                        </RecommendationItem>
                    )) : (
                        <RecommendationEmpty>
                            {candidates.isError ? '추천 대상을 불러오지 못했어요' : candidates.isLoading ? '-' : '오늘 추천할 대상이 없어요'}
                        </RecommendationEmpty>
                    )}
                </RecommendationGrid>
            </RecommendationPanel>

            <Actions>
                <RecommendationButton type="button" onClick={() => goToGifticon('home')}>
                    추천 대상 {recommendCountText} 확인
                    <ArrowRight size={14} />
                </RecommendationButton>
                <PrimaryButton type="button" onClick={openRecommendedSendModal}>
                    <Gift size={16} />
                    바로 선물하기
                </PrimaryButton>
            </Actions>
        </Card>
    );
};

const Card = styled.section`
    position: relative;
    z-index: 1;
    width: 100%;
    padding: 18px;
    box-sizing: border-box;
    overflow: hidden;
    border: 1px solid #dce7f5;
    border-radius: 28px;
    background: linear-gradient(135deg, #ffffff 0%, #f8fbff 50%, #edf5ff 100%);
    box-shadow: 0 24px 48px rgba(15, 23, 42, 0.06);

    &::before {
        position: absolute;
        top: -70px;
        right: -45px;
        width: 220px;
        height: 220px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(191, 219, 254, 0.34) 0%, rgba(191, 219, 254, 0) 70%);
        content: '';
    }

    & > * {
        position: relative;
        z-index: 1;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
`;

const TitleArea = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Title = styled.h2`
    margin: 0;
    color: #111827;
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.8px;
`;

const NewBadge = styled.span`
    padding: 3px 7px;
    border-radius: 999px;
    color: #3d73dd;
    background: #eaf1ff;
    font-size: 9px;
    font-weight: 800;
`;

const HistoryButton = styled.button`
    display: inline-flex;
    height: 32px;
    flex-shrink: 0;
    align-items: center;
    gap: 4px;
    padding: 0 10px;
    border: 1px solid #d5deea;
    border-radius: 8px;
    color: #526176;
    background: #ffffff;
    font-size: 11px;
    font-weight: 800;

    &:hover {
        border-color: #a9bfe8;
        color: #3d73dd;
        background: #f7faff;
    }
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-top: 14px;
`;

const SummaryItem = styled.div<{ $tone: 'blue' | 'default' | 'orange' }>`
    min-width: 0;
    padding: 10px 12px;
    border: 1px solid ${({ $tone }) => ($tone === 'blue' ? '#d4e1f8' : $tone === 'orange' ? '#f8dfab' : '#e4eaf2')};
    border-radius: 10px;
    background: ${({ $tone }) => ($tone === 'blue' ? '#f3f7ff' : $tone === 'orange' ? '#fffaf0' : '#f8fafc')};
`;

const SummaryLabel = styled.p`
    margin: 0;
    color: #758298;
    font-size: 10px;
    font-weight: 700;
`;

const SummaryValue = styled.p`
    margin: 3px 0 0;
    color: #182230;
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.35px;
`;

const SummaryDescription = styled.p`
    display: flex;
    align-items: center;
    gap: 3px;
    margin: 2px 0 0;
    color: #8995a7;
    font-size: 9.5px;
`;

const RecommendationPanel = styled.div`
    margin-top: 9px;
    padding: 11px 12px;
    border: 1px solid #e4eaf2;
    border-radius: 10px;
    background: #f8fafc;
`;

const RecommendationHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const RecommendationTitle = styled.h3`
    display: flex;
    align-items: center;
    gap: 5px;
    margin: 0;
    color: #39475b;
    font-size: 11px;
    font-weight: 800;

    svg {
        color: #7c5cd6;
    }
`;

const RecommendationDescription = styled.p`
    margin: 0;
    color: #929daf;
    font-size: 9.5px;
`;

const RecommendationGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 7px;
    margin-top: 8px;
`;

const RecommendationItem = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 7px;
    padding: 7px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #ffffff;
`;

const Avatar = styled.span`
    display: flex;
    width: 27px;
    height: 27px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #3d73dd;
    background: #eaf1ff;
    font-size: 10px;
    font-weight: 800;
`;

const RecommendationText = styled.div`
    min-width: 0;
    flex: 1;

    b,
    span {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    b {
        color: #283548;
        font-size: 10.5px;
    }

    span {
        margin-top: 1px;
        color: #7c899d;
        font-size: 9px;
    }
`;

const CategoryBadge = styled.span`
    flex-shrink: 0;
    padding: 3px 5px;
    border-radius: 999px;
    color: #66758c;
    background: #eef2f7;
    font-size: 8.5px;
    font-weight: 700;
`;

const RecommendationEmpty = styled.p`
    grid-column: 1 / -1;
    margin: 0;
    padding: 10px 0;
    color: #929daf;
    font-size: 10.5px;
    text-align: center;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 10px;
`;

const RecommendationButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    border: 0;
    color: #3d73dd;
    background: transparent;
    font-size: 10.5px;
    font-weight: 800;
`;

const PrimaryButton = styled.button`
    display: inline-flex;
    height: 34px;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0 14px;
    border: 0;
    border-radius: 8px;
    color: #ffffff;
    background: #3d73dd;
    font-size: 11px;
    font-weight: 800;

    &:hover {
        background: #3265c7;
    }
`;

export default DashboardGifticonSummary;
